import { Alignment, Borders, Cell, FillPattern, Font, Row, Workbook, Worksheet } from 'exceljs';
import { get, set } from 'lodash';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { convertNumberToFourDigitsString, formatDateToDisplay, formatDateToVTextField } from '@/helpers';
import { ASSESSMENT_PERIOD_STATUSES, PROJECT_TYPES } from '@/constants';
import { AssessmentPeriodDTO, ProjectDTO } from '@/models';
import AssessmentPeriodExport from '@/services/assessment-period-export';
import { NA, STYLES } from './project-exporting-config';
import { getUserDisplayInfo } from './user';

type Style = {
  fill?: FillPattern;
  border?: Partial<Borders>;
  font?: Partial<Font>;
  alignment?: Partial<Alignment>;
};

type callbackFn = (cell: Cell, index: number) => void;
const EMPTY = '';

export default class ExportByProject {
  private workbook = new Workbook();
  private ws: Worksheet;
  private readonly assessmentPeriods: AssessmentPeriodDTO[] = [];
  private projectsExport: ProjectDTO[] = [];
  private columnWidths = [8, ...Array(6).fill(30)];

  constructor(assessmentPeriods: AssessmentPeriodDTO[], projects: ProjectDTO[]) {
    this.assessmentPeriods = assessmentPeriods;
    this.projectsExport = projects;
  }

  async generateXLSXFile(): Promise<void> {
    if (this.assessmentPeriods.length >= 2) {
      const sheetName = AssessmentPeriodExport.buildAssessmentSheetName('Summary Information');
      this.ws = this.workbook.addWorksheet(sheetName);
      this.addProjectInformationToWorksheet(this.projectsExport, true);
      this.setColumnWidth();
    }
    this.assessmentPeriods.forEach(period => {
      this.generatePeriod(period);
    });
    try {
      const buf = await this.workbook.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, this.exportFileName);
    } catch (error) {
      console.log(error);
    }
  }

  generatePeriod(period: AssessmentPeriodDTO): void {
    const sheetName = AssessmentPeriodExport.buildAssessmentSheetName(period.name);
    this.ws = this.workbook.addWorksheet(sheetName);
    const projectsOfPeriod = this.projectsExport.filter(proj =>
      proj.projectAssessments.some(pa => pa.assessmentPeriodId === period.id)
    );
    let totalDrop = 0;
    projectsOfPeriod.forEach(proj => {
      proj.projectAssessments.forEach(pa => {
        if (pa.finalDrops) {
          totalDrop += pa.finalDrops;
        }
      });
    });
    this.addAssessmentInformationToWorkSheet(period, projectsOfPeriod, totalDrop);
    this.addEmptyRow();
    this.addEmptyRow();
    this.addProjectInformationToWorksheet(projectsOfPeriod, false, period);
    this.setColumnWidth();
  }

  addAssessmentInformationToWorkSheet(
    assessmentPeriod: AssessmentPeriodDTO,
    projects: ProjectDTO[],
    totalAssessmentPeriodDrops: number
  ): void {
    const rows = this.buildAssessmentInformation(assessmentPeriod, projects, totalAssessmentPeriodDrops);
    this.addTitleRow(`Period Information`, rows[0].length, 1);
    this.addEmptyRow();

    rows.forEach((rowData, rowIndex) => {
      if (rowIndex === 0) {
        this.addRow(rowData, { border: STYLES.primaryTealBorderTop, fill: STYLES.lightTealFill }, (cell, index) => {
          if (index === 2) {
            cell.font = STYLES.boldFont;
          }
        });
        return;
      } else if (rowIndex === rows.length - 1) {
        this.addRow(rowData, { border: STYLES.primaryTealBorderBottom, fill: STYLES.lightTealFill }, (cell, index) => {
          if (index === 2) {
            cell.font = STYLES.boldFont;
          }
        });
        return;
      }
      this.interleaveRowStyle(rowData, rowIndex, (cell, index) => {
        if (index === 2) {
          cell.font = STYLES.boldFont;
        }
      });
    });
  }

  addRow(data: any[], style?: Style, callback?: callbackFn): Row {
    const row = this.ws.addRow(data);
    let maxHeight = 20;

    row.eachCell((cell, index) => {
      if (cell.value !== EMPTY) {
        if (typeof cell.value === 'number') {
          cell.alignment = STYLES.middleRightWrappedAlignment;
        } else {
          if (cell.value) {
            const values = cell.value.toString().split(/[\n\r]+/);

            for (const value of values) {
              let height = value.length;

              if (cell.font && cell.font.bold) {
                height *= 1.08;
              }
              maxHeight = Math.max(maxHeight, height);
            }
          }

          cell.alignment = STYLES.middleLeftWrappedAlignment;
        }
        if (cell.value === NA) {
          cell.font = STYLES.naFont;
          cell.alignment = STYLES.middleRightWrappedAlignment;
        }
        this.addStyleToCell(cell, style);
        if (callback) {
          callback(cell, index);
        }
      }
    });
    row.height = maxHeight;
    return row;
  }

  addProjectInformationToWorksheet(
    projects: ProjectDTO[],
    isExportSummary: boolean,
    assessmentPeriod?: AssessmentPeriodDTO
  ): void {
    const header = [
      'No.',
      'Project ID',
      'Project name',
      'Sponsor',
      'Project Manager',
      'Project Pollens',
      'Evaluation Status'
    ];
    this.addTitleRow(`Project list`, header.length, isExportSummary ? 1 : 10);
    this.addEmptyRow();
    this.addTableHeaderRow(header);
    const boldColumnIndex = 6;
    const projectRows = projects
      .sort((preProject: ProjectDTO, currentProject: ProjectDTO) => {
        return ('' + preProject.name).localeCompare(currentProject.name);
      })
      .map((project, index) => {
        if (isExportSummary) {
          return this.buildProjectSummaryInformation(project, index);
        }
        if (assessmentPeriod) {
          return this.buildProjectInformation(assessmentPeriod, project, index);
        }
        return;
      });

    projectRows.forEach((projectRowData, projectIndex) => {
      if (projectRowData) {
        if (projectIndex === projectRows.length - 1) {
          this.addRow(projectRowData, { border: STYLES.primaryTealBorderBottom }, (cell, index) => {
            if (index === boldColumnIndex) {
              cell.font = STYLES.boldFont;
            }
          });
          return;
        }
        this.interleaveRowStyle(projectRowData, projectIndex, (cell, index) => {
          if (index === boldColumnIndex) {
            cell.font = STYLES.boldFont;
          }
        });
      }
    });
  }

  addStyleToCell(cell: Cell, style: Style = {}): void {
    Object.keys(style).map(key => set(cell, key, get(style, key)));
  }

  addTableHeaderRow(header: string[]): void {
    const row = this.ws.addRow(header);
    row.eachCell(cell => {
      cell.font = STYLES.boldFont;
      cell.fill = STYLES.primaryTealFill;
    });
  }

  addTitleRow(title: string, mergeLength: number, rowPosition: number): void {
    const row = this.ws.addRow([title, ...Array(mergeLength - 2).fill('')]);
    this.mergeCells(row, 1, mergeLength);
    const address = `${row.getCell(1).address}`;
    this.ws.getCell(address).font = STYLES.h1Font;
    this.ws.getRow(rowPosition).height = 45;
  }

  addEmptyRow(): void {
    this.ws.addRow([]);
  }

  buildAssessmentInformation(
    assessmentPeriod: AssessmentPeriodDTO,
    projects: ProjectDTO[],
    totalAssessmentPeriodDrops: number
  ): any[] {
    const { name, startDate, endDate } = assessmentPeriod;
    const result = [
      ['Name', name],
      ['Start date', formatDateToDisplay(startDate)],
      ['End date', formatDateToDisplay(endDate)],
      ['Total projects', projects.length],
      ['Total pollens', totalAssessmentPeriodDrops || NA]
    ];
    return result.map(assessmentInformation => [...Array(1).fill(EMPTY), ...assessmentInformation]);
  }

  buildProjectSummaryInformation(project: ProjectDTO, index: number): any[] {
    let projectTotalDrops = 0;
    this.assessmentPeriods.forEach(ap => {
      const projectDrop = this.getProjectDrop(ap, project) || 0;
      projectTotalDrops += projectDrop;
    });
    return [
      index + 1,
      convertNumberToFourDigitsString(project.id),
      project.name,
      getUserDisplayInfo(project.sponsor),
      getUserDisplayInfo(project.manager),
      projectTotalDrops,
      NA
    ];
  }

  buildProjectInformation(assessmentPeriod: AssessmentPeriodDTO, project: ProjectDTO, index: number): any[] {
    const { DONE } = ASSESSMENT_PERIOD_STATUSES;
    const status = assessmentPeriod.status === DONE.label ? DONE.label : project.projectStatus;
    return [
      index + 1,
      convertNumberToFourDigitsString(project.id),
      project.name,
      getUserDisplayInfo(project.sponsor),
      getUserDisplayInfo(project.manager),
      this.getProjectDrop(assessmentPeriod, project) || NA,
      status
    ];
  }

  mergeCells(row: Row, from: number, to: number): void {
    this.ws.mergeCells(`${row.getCell(from).address}:${row.getCell(to).address}`);
  }

  interleaveRowStyle(data: any[], index: number, callbackFn?: callbackFn): Row {
    return index % 2 === 0
      ? this.addRow(data, { fill: STYLES.lightTealFill }, callbackFn)
      : this.addRow(data, {}, callbackFn);
  }

  setColumnWidth(): void {
    this.ws.columns.forEach((column, index) => {
      column.width = this.columnWidths[index];
    });
  }

  getProjectDrop(assessmentPeriod: AssessmentPeriodDTO, project: ProjectDTO): number | null {
    const assessmentPeriodId = assessmentPeriod.id;
    const currentProjectAssessment = project.projectAssessments.find(
      projectAssessment => projectAssessment.assessmentPeriodId === assessmentPeriodId
    );
    if (currentProjectAssessment) {
      if (project.projectType === PROJECT_TYPES.INDIVIDUAL_TRACKING) {
        return (
          currentProjectAssessment.userEngagements.reduce((prev, userEngagement) => {
            return userEngagement.individualDrops ? userEngagement.individualDrops + prev : prev;
          }, 0) || null
        );
      }
      return currentProjectAssessment.finalDrops;
    }
    return null;
  }

  get exportFileName(): string {
    const currentDate = moment(new Date()).format('YYYYMMDD');
    return `HIVE_Export By Project_${currentDate}.xlsx`;
  }
}
